// src/components/RecruiterPrompt.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Stack, Autocomplete
} from "@mui/material";

const LS_KEY = "recruiterPrompt.v1.dismissedAt";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours

export default function RecruiterPrompt() {
  const [open, setOpen] = useState(false);
  const [isRecruiter, setIsRecruiter] = useState<boolean | null>(null);

  // champs
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [hasPostal, setHasPostal] = useState(true);

  // mode CP + ville (via API)
  const [postal, setPostal] = useState("");
  const [city, setCity] = useState("");
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // mode sans CP (villeTextlibre)
  const [villeTextlibre, setVilleTextlibre] = useState("");

  useEffect(() => {
    const ts = Number(localStorage.getItem(LS_KEY) || 0);
    if (!ts || Date.now() - ts > TTL_MS) setOpen(true);
  }, []);
  const dismissForAWhile = () => { localStorage.setItem(LS_KEY, String(Date.now())); setOpen(false); };

  const isEmailOk = (v:string) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isPostalOk = (v:string) => /^\d{5}$/.test(v);
  const postalValid = isPostalOk(postal); // true si 5 chiffres

  const canSubmit = useMemo(() => {
    if (!company.trim()) return false;
    if (!isEmailOk(email)) return false;
    if (hasPostal) return isPostalOk(postal) && !!city.trim();
    return villeTextlibre.trim().length > 0 && villeTextlibre.trim().length <= 50;
  }, [company, email, postal, city, hasPostal, villeTextlibre]);

  // Récupération des villes pour un CP (API officielle geo.api.gouv.fr)
  useEffect(() => {
    if (!hasPostal || !isPostalOk(postal)) {
      setCityOptions([]);
      setCity("");
      return;
    }
    const controller = new AbortController();
    setLoadingCities(true);
    fetch(`https://geo.api.gouv.fr/communes?codePostal=${postal}&fields=nom&format=json`, {
      signal: controller.signal,
    })
      .then(r => r.ok ? r.json() : [])
      .then((rows: Array<{ nom: string }>) => {
        const opts = Array.from(new Set(rows.map(r => r.nom))).sort((a,b)=>a.localeCompare(b,'fr'));
        setCityOptions(opts);
        // si la ville choisie n'existe plus dans la liste, on réinitialise
        if (opts.length && !opts.includes(city)) setCity("");
      })
      .catch(() => setCityOptions([]))
      .finally(() => setLoadingCities(false));
    return () => controller.abort();
  }, [hasPostal, postal]); // eslint-disable-line react-hooks/exhaustive-deps

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  const payload = {
    denomination: company,
    email,
    codePostal: hasPostal ? postal : null,
    ville: hasPostal ? city : null,
    villeTextlibre: hasPostal ? null : villeTextlibre.trim(),
  };

  try {
    const r = await fetch('/api/recruteurs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error(String(r.status));
    // TODO: Snackbar "Merci !" si tu veux
    dismissForAWhile();
  } catch (err) {
    alert("Erreur d'envoi. Réessaie plus tard.");
  }
};


  const switchMode = () => {
    setHasPostal(v => !v);
    // nettoie les champs de l’autre mode
    setPostal(""); setCity(""); setCityOptions([]); setVilleTextlibre("");
  };

  return (
    <Dialog open={open} onClose={dismissForAWhile} fullWidth maxWidth="xs">
      <DialogTitle>Vous êtes un recruteur&nbsp;?</DialogTitle>

      <DialogContent>
        {isRecruiter === null && (
          <Typography>Souhaitez-vous me laisser vos coordonnées pour un premier contact&nbsp;?</Typography>
        )}

        {isRecruiter === true && (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, display: "grid", gap: 2 }}>
            <TextField
              label="Dénomination de l'entreprise"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
              autoFocus
            />

            <TextField
              label="Email (facultatif)"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!email && !isEmailOk(email)}
              helperText={!!email && !isEmailOk(email) ? "Email invalide" : " "}
            />

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>
                {hasPostal ? "Avec code postal" : "Sans code postal"}
              </Typography>
              <Button size="small" onClick={switchMode}>
                {hasPostal ? "Je n’ai pas de code postal" : "J’ai un code postal"}
              </Button>
            </Stack>

            {hasPostal ? (
              <>
                <TextField
                  label="Code postal"
                  value={postal}
                  onChange={(e) => setPostal(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  inputProps={{ inputMode: "numeric", pattern: "\\d{5}", maxLength: 5 }}
                  required
                  helperText="5 chiffres"
                />

            <Autocomplete
                options={postalValid ? cityOptions : []}
                loading={loadingCities}
                loadingText="Chargement…"
                noOptionsText={postalValid ? "Aucune ville trouvée pour ce code postal" : "Code postal non saisi"}
                value={city || null}
                onChange={(_, v) => setCity(v || "")}
                disabled={!postalValid}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Ville"
                        required
                        disabled={!postalValid}
                        helperText={postalValid ? " " : "Code postal non saisi"}
                    />
                )}
            />

              </>
            ) : (
              <TextField
                label="Ville"
                value={villeTextlibre}
                onChange={(e) => setVilleTextlibre(e.target.value.slice(0, 50))}
                required
                helperText={`${villeTextlibre.length}/50`}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isRecruiter === null ? (
          <>
            <Button onClick={dismissForAWhile} color="inherit">Non merci</Button>
            <Button onClick={() => setIsRecruiter(true)} variant="contained">Oui</Button>
          </>
        ) : (
          <>
            <Button onClick={dismissForAWhile} color="inherit">Annuler</Button>
            <Button type="submit" onClick={handleSubmit} disabled={!canSubmit} variant="contained">
              Envoyer
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
